import { Button, Drawer, Field, TextArea, TextInput, useFeedback } from "../../../../ds";
import { ISpaceService, spaceService } from "../../services/SpaceService";
import { SpaceFieldErrors, SpaceFields, hasErrors, validateSpaceFields } from "../../utils/spaceFields";
import { useCallback, useState } from "react";

import { SpaceCreateResponseDTO } from "../../models/api/SpaceCreateResponseDTO";

interface IAddSpaceDrawerProps {
    open: boolean;
    onClose: () => void;
    /** After the space was created (the home reloads its list). */
    onCreated: (space: SpaceCreateResponseDTO) => void;
    /** Injected in tests; the shared client otherwise. */
    service?: ISpaceService;
}

const emptyFields: SpaceFields = { title: "", description: "", projectId: "" };

/**
 * "New space" (board `Ark-NewSpace`), opened from the Spaces home as a library `Drawer`: Title, Description, optional Project ID
 * (a GUID), then `POST api/Spaces`. Campaign and Expedition on the board are not in the API yet (Ark.Core has no such fields).
 */
export function AddSpaceDrawer(props: IAddSpaceDrawerProps) {
    const { open, onClose, onCreated } = props;
    const service = props.service ?? spaceService();
    const feedback = useFeedback();
    const [fields, setFields] = useState<SpaceFields>(emptyFields);
    const [errors, setErrors] = useState<SpaceFieldErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);

    // closing clears the form, so the drawer always opens empty
    const close = useCallback(() => {
        setFields(emptyFields);
        setErrors({});
        setSubmitted(false);
        setIsBusy(false);
        onClose();
    }, [onClose]);

    const update = (key: keyof SpaceFields, value: string) => {
        const next = { ...fields, [key]: value };
        setFields(next);
        // after the first attempt the messages follow the input
        if (submitted) setErrors(validateSpaceFields(next));
    };

    const handleCreate = useCallback(() => {
        setSubmitted(true);
        const found = validateSpaceFields(fields);
        setErrors(found);
        if (hasErrors(found)) return;

        setIsBusy(true);
        service
            .createSpace({
                title: fields.title.trim(),
                description: fields.description.trim(),
                projectId: fields.projectId.trim() || undefined,
            })
            .then((created) => {
                setIsBusy(false);
                feedback.success(`You have created a Space successfully with ID: ${created.spaceId}`, "Space created");
                onCreated(created);
                close();
            })
            .catch((err) => {
                setIsBusy(false);
                feedback.error(err, "The space was not created");
            });
    }, [fields, service, feedback, onCreated, close]);

    return (
        <Drawer
            open={open}
            onClose={close}
            eyebrow="Spaces"
            title="New space"
            lead="A space holds a tree of pages. Who can read and edit it is managed in Identity Web after you create it."
            footer={
                <>
                    <Button text="Cancel" type="secondary" onClick={close} isDisabled={isBusy} />
                    <Button text="Create space" type="primary" onClick={handleCreate} isBusy={isBusy} />
                </>
            }
        >
            <Field label="Title" required error={errors.title}>
                <TextInput value={fields.title} onChange={(v) => update("title", v)} placeholder="Insert a title" autoFocus
                    hasError={!!errors.title} disabled={isBusy} />
            </Field>
            <Field label="Description" required error={errors.description}>
                <TextArea value={fields.description} onChange={(v) => update("description", v)} placeholder="Insert a description" rows={3}
                    hasError={!!errors.description} disabled={isBusy} />
            </Field>
            <Field label="Project ID (optional)" error={errors.projectId} hint="Optional GUID of the Azure DevOps project this space documents.">
                <TextInput value={fields.projectId} onChange={(v) => update("projectId", v)} placeholder="Insert a GUID"
                    hasError={!!errors.projectId} disabled={isBusy} className="ark-mono" />
            </Field>
        </Drawer>
    );
}
